import { useTransition } from 'react'
import type { ManagerId } from '@ludus/core'
import { useStageState } from '../useStageState.ts'
import { EMPTY_FILTER_DRAFT, type FilterDraft } from '../../types.ts'
import { FilterByManager } from './FilterByManager.tsx'
import { FilterByPeriod } from './FilterByPeriod.tsx'
import { SearchInput } from './SearchInput.tsx'

/**
 * Inline filter group rendered inside the focused-stage header. Search + owner
 * multi-select + period preset + reset. Layout is wrapping flex; on narrow viewports
 * the chips drop to a second row inside the header.
 *
 * Filter dispatches are wrapped in `startTransition` so typing in the search box and
 * picking owners stays responsive while the deals table re-renders as a low-priority
 * update — important once the lead set grows past the mock's ~180 rows.
 */
export function FiltersBar() {
  const filters = useStageState((s) => s.filters)
  const setFilters = useStageState((s) => s.setFilters)
  const toggleManagerFilter = useStageState((s) => s.toggleManagerFilter)
  const resetFilters = useStageState((s) => s.resetFilters)

  const [, startFilterTransition] = useTransition()

  const dispatch = (patch: Partial<FilterDraft>) => {
    startFilterTransition(() => setFilters(patch))
  }
  const dispatchToggleManager = (id: ManagerId) => {
    startFilterTransition(() => toggleManagerFilter(id))
  }
  const dispatchReset = () => {
    startFilterTransition(() => resetFilters())
  }

  const isDirty =
    filters.managerIds.length > 0 ||
    filters.search.length > 0 ||
    filters.period !== EMPTY_FILTER_DRAFT.period

  return (
    <div className="flex flex-wrap items-center gap-2">
      <SearchInput
        value={filters.search}
        onChange={(search) => dispatch({ search })}
        className="w-full sm:w-56"
      />
      <FilterByManager
        selected={filters.managerIds}
        onToggle={dispatchToggleManager}
        onReset={() => dispatch({ managerIds: [] })}
      />
      <FilterByPeriod value={filters.period} onChange={(period) => dispatch({ period })} />
      {isDirty && (
        <button
          type="button"
          onClick={dispatchReset}
          className="px-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted transition-colors hover:text-foreground"
        >
          Reset
        </button>
      )}
    </div>
  )
}
